import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import L from 'leaflet';
import type { Vendor, Position } from '../../../types';
import { projectToNewLayerPoint } from '../../../lib/map/coords';
import { clampScale, LABEL_MIN_ZOOM } from './markerDom';
import styles from './ScaledMarkerLayer.module.css';

interface Options {
  map: L.Map;
  vendorByPositionUid: Map<string, Vendor>;
  positionByUid: Map<string, Position>;
  markerElsRef: MutableRefObject<Map<string, HTMLElement>>;
  placeholderElsRef: MutableRefObject<Map<string, HTMLElement>>;
  markerLabelElsRef: MutableRefObject<Map<string, HTMLElement>>;
  placeholderLabelElsRef: MutableRefObject<Map<string, HTMLElement>>;
}

export function useZoomAnimation({
  map,
  vendorByPositionUid,
  positionByUid,
  markerElsRef,
  placeholderElsRef,
  markerLabelElsRef,
  placeholderLabelElsRef,
}: Options) {
  // During zoom animation, project each marker to its destination zoom
  // and set the target transform. The CSS transition on `.zoomingMarker`
  // (matched to Leaflet's built-in zoom curve) glides the marker to its
  // target in sync with the image overlay, which uses Leaflet's own
  // `.leaflet-zoom-animated` class. The Leaflet-private projection
  // lives behind `projectToNewLayerPoint` — a single, well-known leak
  // of internals with a runtime fallback.
  const animateMarkerZoom = useCallback((e: L.ZoomAnimEvent) => {
    const targetZoom = e.zoom;
    const targetCenter = e.center;
    const scale = clampScale(targetZoom);
    const showLabels = targetZoom >= LABEL_MIN_ZOOM;

    markerElsRef.current.forEach((el, posUid) => {
      if (el.dataset.dragging === 'true') return;
      const vendor = vendorByPositionUid.get(posUid);
      if (!vendor) return;
      const point = projectToNewLayerPoint(
        map,
        L.latLng(vendor.coordinates[0], vendor.coordinates[1]),
        targetZoom,
        targetCenter,
      );
      el.classList.add(styles.zoomingMarker!);
      el.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%,-50%) scale(${scale})`;
      const labelEl = markerLabelElsRef.current.get(posUid);
      if (labelEl) labelEl.style.visibility = showLabels ? '' : 'hidden';
    });

    placeholderElsRef.current.forEach((el, uid) => {
      if (el.dataset.dragging === 'true') return;
      const pos = positionByUid.get(uid);
      if (!pos) return;
      const point = projectToNewLayerPoint(
        map,
        L.latLng(pos.coordinates[0], pos.coordinates[1]),
        targetZoom,
        targetCenter,
      );
      el.classList.add(styles.zoomingMarker!);
      el.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%,-50%) scale(${scale})`;
      const labelEl = placeholderLabelElsRef.current.get(uid);
      if (labelEl) labelEl.style.visibility = showLabels ? '' : 'hidden';
    });
  // Refs are stable — intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vendorByPositionUid, positionByUid]);

  const clearZoomTransition = useCallback(() => {
    markerElsRef.current.forEach(el => el.classList.remove(styles.zoomingMarker!));
    placeholderElsRef.current.forEach(el => el.classList.remove(styles.zoomingMarker!));
  // Refs are stable — intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { animateMarkerZoom, clearZoomTransition };
}
